// src/services/sse/hooks/useSSE.ts
/**
 * Generic SSE hook for any stream endpoint.
 * 
 * Uses ConnectionManager to share connections across components.
 */
import { useEffect, useState, useRef } from 'react';
import { connectionManager } from '../ConnectionManager.ts';
import { BaseSSEEvent, UseSSEReturn, SSEOptions, SSEEventType } from '../types.ts';

/**
 * Generic SSE hook for any stream endpoint.
 * 
 * @param endpoint - Stream endpoint path (e.g., 'balance', 'notifications')
 * @param options - Configuration options
 * @returns SSE connection state and data
 */
export const useSSE = <T extends BaseSSEEvent>(
  endpoint: string | null,
  options: SSEOptions = {}
): UseSSEReturn<T> => {
  const {
    maxReconnectAttempts = 5,
    reconnectDelay = 3000,
    onMessage,
    onError,
    onConnect,
    debug = false,
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const retryRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!endpoint) {
      setLoading(false);
      setConnected(false);
      return;
    }

    // Clean up previous subscription
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }

    setLoading(true);
    setError(null);

    // Subscribe to connection manager
    const unsubscribe = connectionManager.subscribe(
      endpoint,
      (eventData: BaseSSEEvent) => {
        // Handle heartbeat
        if (eventData.type === SSEEventType.HEARTBEAT) {
          if (debug) {
            console.debug(`💓 SSE heartbeat: ${endpoint}`);
          }
          return;
        }

        // Handle error events
        if (eventData.type === SSEEventType.ERROR) {
          const errorMessage = eventData.message || 'Connection error';
          setError(errorMessage);
          setLoading(false);
          setConnected(false);
          return;
        }

        // Handle regular events
        setData(eventData as T);
        setLoading(false);
        setError(null);
        setConnected(true);
        onMessage?.(eventData);
      },
      {
        onError: (err) => {
          setConnected(false);
          setLoading(false);
          onError?.(err);
        },
        onConnect: () => {
          setConnected(true);
          setLoading(false);
          onConnect?.();
        },
        maxReconnectAttempts,
        reconnectDelay,
        debug,
      }
    );

    unsubscribeRef.current = unsubscribe;

    // Set up retry function
    retryRef.current = () => {
      // Close and reconnect
      connectionManager.close(endpoint);
      setLoading(true);
      setError(null);
      // Re-subscribe will happen on next render
    };

    // Cleanup on unmount
    return () => {
      unsubscribe();
      unsubscribeRef.current = null;
    };
  }, [endpoint, maxReconnectAttempts, reconnectDelay, debug]);

  return {
    data,
    loading,
    error,
    connected,
    retry: retryRef.current || undefined,
  };
};

